'use client';

import React, { useState } from 'react';
import useUserStore from '@/state/userState';
import posthog from '@/lib/posthog';
import { toast } from '@/utils/toastManager';
import { Copy, Plus, Trash2, RefreshCw, Check } from 'lucide-react';
import ConfirmationModal from '../Settings/modals/ConfirmationModal';
import { SettingsHeader } from '../Settings/components/SettingsHeader';
import { useApiKeys } from '@/hooks/useApiKeys';
import { Skeleton } from '@/components/ui/skeleton';

function maskString(str: string) {
  if (!str) {
    return '';
  }

  if (str.length <= 8) {
    // If string is 8 chars or less, just mask all but show first 2 and last 2
    if (str.length <= 4) {
      return str.replace(/./g, '*');
    }
    const firstTwo = str.slice(0, 2);
    const lastTwo = str.slice(-2);
    const maskedPart = str.slice(2, -2).replace(/./g, '*');
    return firstTwo + maskedPart + lastTwo;
  }

  const firstFour = str.slice(0, 4);
  const lastFour = str.slice(-4);
  const maskedPart = str.slice(4, -4).replace(/./g, '*');

  return firstFour + maskedPart + lastFour;
}

export const ApiKeys = () => {
  const { user } = useUserStore();
  const {
    apiKeys,
    isLoading,
    isGenerating,
    isDeleting,
    generateApiKey,
    deleteApiKey,
  } = useApiKeys();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);

      posthog.capture('api_key_copied', {
        github_username: user?.GithubUsername,
      });

      setCopied(true);

      toast.show({
        title: 'API key copied to clipboard',
        status: 'success',
        isClosable: true,
        duration: 2000,
      });

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.show({
        title: 'Failed to copy',
        description: 'Please try again',
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleDeleteApiKey = () => {
    deleteApiKey();
    setShowDeleteConfirm(false);
  };

  const handleRotateKey = () => {
    generateApiKey();
  };

  return (
    <div className="flex flex-col gap-5 motion-stagger">
      {/* Header Section */}
      <div className="flex items-center justify-between w-full">
        <SettingsHeader
          title="API Keys"
          description="Manage your API key to authenticate with the Entelligence CLI and MCP"
        />
        {!isLoading && apiKeys.length === 0 && (
          <button
            onClick={generateApiKey}
            disabled={isGenerating}
            className="bg-ds-surface-white border border-ds-border-subtler h-[34px] flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-[8px] hover:bg-ds-gray-subtlest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-5 h-5 text-ds-text-darker" />
            <span className="text-[12px] font-medium text-ds-text-darker text-center leading-[14px]">
              Create Key
            </span>
          </button>
        )}
      </div>

      {/* API Key Card */}
      {isLoading ? (
        /* Skeleton — matches real card layout (input row + date + buttons + note) */
        <div className="bg-ds-surface-white border border-ds-border-subtlest rounded-lg shadow-sm p-5 flex flex-col gap-5">
          <div className="flex flex-col gap-[5px]">
            <div className="flex flex-col gap-2">
              <Skeleton className="h-4 w-16 bg-ds-gray-subtlest" />
              <div className="flex gap-3 h-10 items-center">
                <Skeleton className="flex-1 h-10 rounded-[8px] bg-ds-gray-subtlest" />
                <Skeleton className="h-10 w-[88px] rounded-[8px] bg-ds-gray-subtlest" />
              </div>
            </div>
            <div className="flex items-center px-0 py-[3px]">
              <Skeleton className="h-4 w-48 bg-ds-gray-subtlest" />
            </div>
          </div>
          <div className="flex gap-3 items-center">
            <Skeleton className="h-9 w-32 rounded-[8px] bg-ds-gray-subtlest" />
            <Skeleton className="h-9 w-32 rounded-[8px] bg-ds-gray-subtlest" />
          </div>
          <Skeleton className="h-11 rounded-[8px] bg-ds-gray-subtlest" />
        </div>
      ) : apiKeys.length > 0 ? (
        <div className="bg-ds-surface-white border border-ds-border-subtlest rounded-lg shadow-sm p-5 flex flex-col gap-5">
          <div className="flex flex-col gap-[5px]">
            {/* API Key Label and Input */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="api-key-display"
                className="h-4 text-[12px] font-medium text-ds-text-subtle leading-[16px]"
              >
                API KEY
              </label>
              <div className="flex gap-3 h-10 items-center">
                <div
                  id="api-key-display"
                  className="bg-ds-surface-off-white border border-ds-border-subtler flex-1 rounded-[8px] px-[15px] py-[10px]"
                >
                  <p className="font-medium leading-[21px] text-[14px] text-ds-text-darker">
                    {maskString(apiKeys[0]?.apiKey || '')}
                  </p>
                </div>
                <button
                  onClick={() => handleCopy(apiKeys[0]?.apiKey || '')}
                  className={`h-full rounded-[8px] flex items-center gap-[10px] px-[14px] py-[9px] transition-all ${
                    copied
                      ? 'bg-ds-brand-default border border-ds-brand-darker'
                      : 'bg-ds-brand-default border border-ds-brand-default hover:opacity-90 '
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 text-ds-surface-white" />
                      <span className="font-semibold leading-[21px] text-[14px] text-ds-surface-white text-center">
                        Copied!
                      </span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 text-ds-surface-white" />
                      <span className="font-semibold leading-[21px] text-[14px] text-ds-surface-white text-center">
                        Copy
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Created Date */}
            <div className="flex items-center px-0 py-[3px]">
              <div className="flex items-center">
                <span className="font-medium leading-[normal] text-[12px] text-ds-text-subtle">
                  Created:
                </span>
                <span className="font-medium leading-[normal] text-[12px] text-ds-text-darker ml-1">
                  {apiKeys[0]?.createdAt
                    ? new Date(apiKeys[0].createdAt).toLocaleDateString(
                        'en-US',
                        {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        }
                      )
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 items-center">
            <button
              onClick={handleRotateKey}
              disabled={isGenerating}
              className="border border-ds-border-subtler h-9 rounded-[8px] w-32 relative hover:bg-ds-gray-subtlest transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <RefreshCw
                className={`w-4 h-4 text-ds-text-darker ${isGenerating ? 'animate-spin' : ''}`}
              />
              <span className="font-medium leading-[21px] text-[14px] text-ds-text-darker text-center">
                Rotate Key
              </span>
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
              className="border border-ds-border-subtler h-9 rounded-[8px] w-32 relative hover:bg-ds-danger-subtlest/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Trash2 className="w-4 h-4 text-ds-danger-default" />
              <span className="font-medium leading-[21px] text-[14px] text-ds-danger-default text-center">
                Delete Key
              </span>
            </button>
          </div>

          {/* Note Section */}
          <div className="bg-ds-surface-off-white border border-ds-border-subtler rounded-[8px] h-11 flex gap-[5px] items-center p-3">
            <span className="font-bold leading-[18px] text-[12px] text-ds-text-darker shrink-0">
              Note:
            </span>
            <p className="font-medium leading-[14px] text-[12px] text-ds-text-subtle">
              Keep your API key secure. You can change it anytime to generate a
              new key. Deleting the key will revoke access immediately.
            </p>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="bg-ds-surface-white border border-ds-border-subtlest rounded-lg shadow-sm p-5 flex flex-col items-center justify-center py-12 text-center">
          <p className="text-sm text-ds-text-subtle mb-4">
            No API keys created yet. Create your first API key to get started.
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteApiKey}
        title="Delete API Key?"
        message="Are you sure you want to delete your API key? This action cannot be undone. Any applications using this key will stop working."
        confirmText="Delete"
        cancelText="Cancel"
        isDanger={true}
      />
    </div>
  );
};
